import { useQuery } from "@apollo/client";
import {
  GetOrganizationSettingsQuery,
  GetOrganizationSettingsQueryVariables,
  OrgSettingsOrganizationFragmentFragment,
} from "types/graphql-schema";

import getOrganizationSettingsQuery from "@apps/org-settings/graphql/get-organization-settings-query";
import { role } from "@helpers/constants";

type UseAdminOrganizationResult = {
  organization: OrgSettingsOrganizationFragmentFragment | null;
  orgId: number | null;
  role: string | null;
  isItAdmin: boolean;
  canManageOrgAi: boolean;
  loading: boolean;
};

/**
 * Picks the org the current user can administer (same selection logic as
 * sidebar-settings-expanded.tsx): the first non-personal org where the user is
 * a global / people / IT admin. `canManageOrgAi` is true only for global/people
 * admins (IT admins can't manage org AI settings).
 */
const useAdminOrganization = (): UseAdminOrganizationResult => {
  const { data, loading } = useQuery<
    GetOrganizationSettingsQuery,
    GetOrganizationSettingsQueryVariables
  >(getOrganizationSettingsQuery);

  const organization =
    (data?.me?.organizations?.edges ?? []).find(
      (edge) =>
        edge?.node &&
        !edge.node.personal &&
        [role.globalAdmin, role.peopleAdmin, role.itAdmin].includes(
          edge.node.userMembership?.role ?? "",
        ),
    )?.node ?? null;

  const membershipRole = organization?.userMembership?.role ?? null;
  const isItAdmin = membershipRole === role.itAdmin;
  const canManageOrgAi =
    membershipRole === role.globalAdmin || membershipRole === role.peopleAdmin;

  return {
    organization,
    orgId: organization?.id ?? null,
    role: membershipRole,
    isItAdmin,
    canManageOrgAi,
    loading,
  };
};

export default useAdminOrganization;
